import {
  GridApi,
  IRowNode,
  IServerSideGroupSelectionState,
  IServerSideSelectionState,
} from 'ag-grid-community';
import { useMemo, ButtonHTMLAttributes, useEffect, useReducer } from 'react';
import { BulkActionsBase as BulkActions } from '@glu/data-components';
import { useComposedGridContext } from 'ComposedGrid/ComposedGridContext';

export interface IBulkActionsAgGridProps {
  /** Bulk actions definition. Note that selection will be passed back on the onClick */
  actions: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> &
    {
      label: string;
      onClick(
        selection:
          | ReturnType<GridApi['getSelectedNodes']>
          | ReturnType<GridApi['getServerSideSelectionState']>
      ): void;
      /** Allows for conditional enabling and disabling of bulk action based on
       * the rows selected. Return true to disable the bulk action.
       */
      getDisabled?(selection: TSelection): boolean;
    }[];
}

type TSelection =
  | IRowNode<unknown>[]
  | IServerSideSelectionState
  | IServerSideGroupSelectionState
  | null;

export const useBulkActionsAgGrid = ({ actions }: IBulkActionsAgGridProps) => {
  const { gridRef } = useComposedGridContext();
  const [, forceUpdate] = useReducer((i) => i + 1, 0);
  const isServerSide = gridRef.current.props.rowModelType === 'serverSide';
  const selection = isServerSide
    ? gridRef.current.api.getServerSideSelectionState()
    : gridRef.current.api.getSelectedNodes();
  const totalRows = gridRef.current.api.paginationGetRowCount();

  const actionsWithProps = useMemo(() => {
    const getDisabled = () => {
      if (!selection) {
        return true;
      }
      // client side and no rows selected
      if (Array.isArray(selection)) {
        return selection.length === 0;
      }
      // server side and no manually selected/deselected rows
      const hasNoToggledNodes = selection.toggledNodes?.length === 0;

      // server side groups with groupSelectsChildren={true}
      if ('selectAllChildren' in selection) {
        return hasNoToggledNodes && !selection.selectAllChildren;
      }
      return hasNoToggledNodes && !(selection as IServerSideSelectionState).selectAll;
    };

    const disabled = getDisabled();

    return actions.map((action) => {
      const { getDisabled: getDisabledProp, ...rest } = action;
      return {
        disabled: getDisabledProp?.(selection) ?? disabled,
        ...rest,
        onClick: () => {
          action.onClick(selection);
        },
      };
    });
  }, [actions, selection]);

  const selectionLength = useMemo(() => {
    if (!selection) {
      return 0;
    }
    // client side
    if (Array.isArray(selection)) {
      return selection.length;
    }
    // server side
    if ('selectAll' in selection) {
      if (selection.selectAll) {
        return totalRows - selection.toggledNodes.length;
      }
      return selection.toggledNodes.length;
    }
    // when groupSelectsChildren={true} we currently can't determine selected count
    return 0;
  }, [selection, totalRows]);

  /**
   * Keeps ag-grid as the primary source of truth without having to duplicate state
   * The forceUpdate is unfortunate, but better than the alternative of duplicating state
   */
  useEffect(() => {
    const currRef = gridRef.current;
    const onAgGridPageChange = () => {
      forceUpdate();
    };
    if (currRef.api) {
      currRef.api.addEventListener('selectionChanged', onAgGridPageChange);
    }
    return () => {
      currRef.api.removeEventListener('selectionChanged', onAgGridPageChange);
    };
  }, [gridRef]);

  return useMemo(
    () => ({
      actions: actionsWithProps,
      selectionLength,
    }),
    [actionsWithProps, selectionLength]
  );
};

const BulkActionsAgGrid = ({ actions: passedActions }: IBulkActionsAgGridProps) => {
  const { actions, selectionLength } = useBulkActionsAgGrid({ actions: passedActions });
  return <BulkActions actions={actions} selectionLength={selectionLength} />;
};

export default BulkActionsAgGrid;
